<template>
  <DialogBase
    :visible.sync="visible"
    :title="dialogTitle"
    size="lg"
    :confirm-loading="loading"
    confirm-text="Lưu"
    cancel-text="Đóng"
    :use-panel="true"
    panel-title="Nhận xét định kỳ"
    :panel-subtitle="panelSubtitle"
    panel-icon="el-icon-s-claim"
    @confirm="handleSave"
    @cancel="handleClose"
  >
    <el-form
      ref="dataForm"
      :model="dataForm"
      :rules="formRules"
      label-position="top"
      class="sk-u-mt-12"
    >
      <!-- Header fields -->
      <div class="pev-form-header">
        <el-form-item label="Lần đánh giá" prop="periodNumber" class="pev-form-item">
          <el-input
            v-model="dataForm.periodNumber"
            readonly
            style="width: 140px"
          />
        </el-form-item>

        <el-form-item label="Ngày" prop="date" class="pev-form-item">
          <el-date-picker
            v-model="dataForm.date"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd/MM/yyyy"
            placeholder="Chọn ngày"
            :clearable="false"
            style="width: 180px"
          />
        </el-form-item>

        <el-form-item v-if="!isEditMode" label="Chế độ duyệt" class="pev-form-item">
          <el-switch
            v-model="dataForm.approved"
            active-text="Tự duyệt"
            inactive-text="Chờ duyệt"
          />
        </el-form-item>
      </div>

      <!-- Student list for batch create -->
      <div v-if="!isEditMode" class="pev-form-students">
        <div class="pev-form-students__header">
          <p class="pev-form-students__title">
            Nhận xét cho học sinh
            <el-tag size="mini" type="info">{{ validStudentCount }}/{{ dataForm.students.length }} đã nhập</el-tag>
          </p>
          <el-checkbox v-model="showOnlyEmpty" class="pev-form-checkbox">
            Chỉ hiện HS chưa nhập
          </el-checkbox>
        </div>

        <div class="pev-form-students__list">
          <div
            v-for="student in filteredStudents"
            :key="student.studentId"
            class="pev-student-row"
          >
            <div class="pev-student-row__info">
              <span class="pev-student-row__index">{{ getOriginalIndex(student) + 1 }}.</span>
              <span class="pev-student-row__name">{{ student.studentName }}</span>
            </div>
            <el-input
              v-model="student.content"
              type="textarea"
              :rows="2"
              :placeholder="`Nhập nhận xét cho ${student.studentName}...`"
              maxlength="2000"
              show-word-limit
              class="pev-student-row__input"
            />
          </div>
        </div>
      </div>

      <!-- Single student edit -->
      <div v-else class="pev-form-edit">
        <div class="pev-form-edit__student">
          <i class="el-icon-user" />
          <span>{{ dataForm.students[0] && dataForm.students[0].studentName }}</span>
        </div>
        <el-form-item label="Nội dung nhận xét" prop="editContent">
          <el-input
            v-model="dataForm.editContent"
            type="textarea"
            :rows="6"
            placeholder="Nhập nội dung nhận xét..."
            maxlength="2000"
            show-word-limit
          />
        </el-form-item>
      </div>

      <!-- File/ảnh đính kèm (Ở DƯỚI CÙNG SAU CÁC NHẬN XÉT) -->
      <div class="evl-upload-section">
        <div class="evl-upload-section__header">
          <span class="evl-upload-title">
            <i class="el-icon-paperclip" /> File & hình ảnh đính kèm
          </span>
          <span class="evl-upload-subtitle">Tối đa 3 file/nhận xét (dùng chung cho các học sinh đã chọn)</span>
        </div>
        <el-upload
          drag
          action="#"
          :auto-upload="false"
          :file-list="fileList"
          :limit="3"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          multiple
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :on-exceed="handleFileExceed"
          class="evl-drag-uploader"
        >
          <i class="el-icon-upload" />
          <div class="el-upload__text">
            Kéo thả file/ảnh vào đây hoặc <em>nhấn để chọn file</em>
          </div>
        </el-upload>
      </div>
    </el-form>
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import PeriodicEvaluationService from "@/modules/evaluation/services/PeriodicEvaluationService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import moment from "moment";
import { EVALUATION_FILE_MAX_COUNT, isEvaluationFileAllowed } from "@/modules/evaluation/utils/evaluationFile";

export default {
  name: "PeriodicEvaluationFormDialog",
  components: { DialogBase },
  props: {
    classId: { type: [Number, String], default: "" },
    className: { type: String, default: "" },
    gradeId: { type: [Number, String], default: "" },
    year: { type: [Number, String], default: "" },
  },
  data() {
    return {
      visible: false,
      loading: false,
      isEditMode: false,
      editUid: null,
      originalEditContent: "",
      showOnlyEmpty: false,
      fileList: [],
      originalFiles: [],
      dataForm: {
        periodNumber: 1,
        date: moment().format("YYYY-MM-DD"),
        approved: true,
        students: [],
        editContent: "",
      },
      formRules: {
        periodNumber: [{ required: true, message: "Vui lòng chọn lần", trigger: "change" }],
        date: [{ required: true, message: "Vui lòng chọn ngày", trigger: "change" }],
        editContent: [
          { required: true, message: "Vui lòng nhập nội dung nhận xét", trigger: "blur" },
          { max: 2000, message: "Tối đa 2000 ký tự", trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.isEditMode ? "Chỉnh sửa nhận xét định kỳ" : "Tạo nhận xét định kỳ";
    },
    panelSubtitle() {
      return this.isEditMode
        ? "Chỉnh sửa nội dung nhận xét định kỳ"
        : "Tạo nhận xét cho một hoặc nhiều học sinh";
    },
    schoolId() {
      return this.$store.state.auth.user.schoolId;
    },
    validStudentCount() {
      return this.dataForm.students.filter(s => s.content && s.content.trim()).length;
    },
    filteredStudents() {
      if (!this.showOnlyEmpty) return this.dataForm.students;
      return this.dataForm.students.filter(s => !s.content || !s.content.trim());
    },
  },
  methods: {
    /**
     * Open for batch create
     * @param {Array} studentList - List of students [{studentId, studentName}]
     * @param {Number} defaultPeriodNumber - Auto-calculated next period
     * @param {Object} [preselectedStudent] - If provided, only this student
     */
    openForCreate(studentList, defaultPeriodNumber, preselectedStudent) {
      this.isEditMode = false;
      this.editUid = null;
      this.originalEditContent = "";
      this.showOnlyEmpty = false;
      this.fileList = [];
      this.originalFiles = [];

      const students = preselectedStudent
        ? [{ studentId: preselectedStudent.studentId, studentName: preselectedStudent.studentName, content: "", fileUrls: [] }]
        : studentList.map(s => ({ studentId: s.studentId, studentName: s.studentName, content: "", fileUrls: [] }));

      this.dataForm = {
        periodNumber: defaultPeriodNumber || 1,
        date: moment().format("YYYY-MM-DD"),
        approved: true,
        students: students,
        editContent: "",
      };

      this.visible = true;
      this.$nextTick(() => {
        this.$refs.dataForm?.clearValidate();
      });
    },

    /**
     * Open for edit a single evaluation
     * @param {Object} studentData - { studentId, studentName }
     * @param {Object} evaluation - The evaluation object to edit
     */
    openForEdit(studentData, evaluation) {
      this.isEditMode = true;
      this.editUid = evaluation.uid;
      this.originalEditContent = (evaluation.content || "").trim();
      this.fileList = (evaluation.files || evaluation.fileUrls || []).map((file, index) => ({
        uid: `existing-${index}`,
        name: String(file).split("/").pop(),
        existing: true,
        objectKey: file,
        status: "success",
      }));
      this.originalFiles = this.getFileState();

      this.dataForm = {
        periodNumber: evaluation.periodNumber,
        date: evaluation.date,
        approved: evaluation.approved,
        students: [{ studentId: studentData.studentId, studentName: studentData.studentName, uid: evaluation.uid }],
        editContent: evaluation.content,
      };

      this.visible = true;
      this.$nextTick(() => {
        this.$refs.dataForm?.clearValidate();
      });
    },

    async handleSave() {
      const valid = await this.$refs.dataForm.validate().catch(() => false);
      if (!valid) return;

      if (this.isEditMode
        && (this.dataForm.editContent || "").trim() === this.originalEditContent
        && JSON.stringify(this.getFileState()) === JSON.stringify(this.originalFiles)) {
        NotifyMessage.leftCornerNotificationError("Nội dung không thay đổi, không thể lưu!");
        return;
      }

      this.loading = true;
      try {
        if (this.isEditMode) {
          await this._doUpdate();
        } else {
          await this._doCreate();
        }
        this.$emit("saved");
        this.handleClose();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Có lỗi xảy ra!"
        );
      } finally {
        this.loading = false;
      }
    },

    async _doCreate() {
      const studentsWithContent = this.dataForm.students
        .filter(s => s.content && s.content.trim())
        .map(s => ({
          studentId: s.studentId,
          studentName: s.studentName,
          content: s.content.trim(),
          fileUrls: s.fileUrls || [],
        }));

      if (studentsWithContent.length === 0) {
        NotifyMessage.leftCornerNotificationError("Vui lòng nhập nhận xét cho ít nhất 1 học sinh!");
        throw new Error("No content");
      }

      const payload = {
        classId: Number(this.classId),
        className: this.className,
        gradeId: Number(this.gradeId),
        year: Number(this.year),
        periodNumber: this.dataForm.periodNumber,
        date: this.dataForm.date,
        approved: this.dataForm.approved,
        students: studentsWithContent,
        files: this.getFiles(),
      };

      const res = await PeriodicEvaluationService.create(payload);
      const count = studentsWithContent.length;
      NotifyMessage.leftCornerNotificationSuccess(
        res.data.message || `Tạo nhận xét thành công cho ${count} học sinh!`
      );
    },

    async _doUpdate() {
      const payload = {
        year: Number(this.year),
        students: [{
          studentId: this.dataForm.students[0].studentId,
          uid: this.editUid,
          content: this.dataForm.editContent.trim(),
          files: this.getFiles(),
          existingFiles: this.getExistingFiles(),
        }],
      };

      const res = await PeriodicEvaluationService.update(payload);
      NotifyMessage.leftCornerNotificationSuccess(res.data.message || "Cập nhật thành công!");
    },

    handleClose() {
      this.$refs.dataForm?.resetFields();
      this.visible = false;
    },

    getOriginalIndex(student) {
      return this.dataForm.students.findIndex(s => s.studentId === student.studentId);
    },

    handleFileChange(file, fileList) {
      if (!isEvaluationFileAllowed(file.raw)) {
        NotifyMessage.leftCornerNotificationError("Chỉ được tải ảnh, file Word, Excel hoặc PDF!");
        this.fileList = fileList.filter(item => item.uid !== file.uid);
        return;
      }
      if (fileList.length > EVALUATION_FILE_MAX_COUNT) {
        NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
        this.fileList = fileList.slice(0, EVALUATION_FILE_MAX_COUNT);
        return;
      }
      this.fileList = fileList;
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList;
    },

    handleFileExceed() {
      NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
    },

    getFiles() {
      return this.fileList.map(file => file.raw).filter(Boolean);
    },

    getExistingFiles() {
      return this.fileList.filter(file => file.existing).map(file => file.objectKey);
    },

    getFileState() {
      return this.fileList.map(file => file.existing
        ? `existing:${file.objectKey}`
        : `new:${file.name}:${file.size || file.raw?.size || 0}:${file.raw?.lastModified || 0}`
      ).sort();
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

.pev-form-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.pev-form-item {
  margin-bottom: 0;
}

/* ─── Students list (batch create) ─────────────── */
.pev-form-students {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__list {
    max-height: 450px;
    overflow-y: auto;
    padding-right: 4px;
  }
}

.pev-form-checkbox {
  font-size: 13px;
}

.pev-student-row {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  transition: border-color 0.15s;

  &:hover {
    border-color: #cbd5e1;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__index {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 600;
    min-width: 24px;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  &__input {
    /deep/ .el-textarea__inner {
      border-radius: 8px;
    }
  }
}

/* ─── Single edit ──────────────────────────────── */
.pev-form-edit {
  &__student {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;

    i {
      color: #2563eb;
      font-size: 18px;
    }
  }
}

/* ─── Modern Drag Dropzone ───────────────────────── */
.evl-upload-section {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
}

.evl-upload-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.evl-upload-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #2563eb;
    font-size: 15px;
  }
}

.evl-upload-subtitle {
  font-size: 12px;
  color: #94a3b8;
}

.evl-drag-uploader {
  width: 100%;

  /deep/ .el-upload {
    width: 100%;
  }

  /deep/ .el-upload-dragger {
    width: 100%;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    transition: all 0.2s ease;

    &:hover {
      border-color: #3b82f6;
      background: #eff6ff;
    }

    .el-icon-upload {
      font-size: 32px;
      color: #3b82f6;
      margin: 0 0 2px;
      line-height: 1;
    }

    .el-upload__text {
      font-size: 13px;
      color: #64748b;
      line-height: 1.4;

      em {
        color: #2563eb;
        font-weight: 600;
        font-style: normal;
      }
    }
  }

  /deep/ .el-upload-list {
    margin-top: 8px;

    .el-upload-list__item {
      border-radius: 8px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      margin-top: 6px;
      transition: all 0.15s ease;

      &:hover {
        background: #e2e8f0;
      }
    }
  }
}
</style>
